// components/MyIntakeScreen.jsx
// =============================================================================
// GENEROSITY WTR APP - MY INTAKE (FREE TIER CENTER TAB, Commit 6)
//
// The free counterpart to My Hydration, per the tier spec:
//   FREE  = intake:      what you drank. Big number is TODAY'S OUNCES.
//   PLUS  = absorption:  what your body kept. Big number is the SCORE.
//
// Anatomy (same visual weight and positions as My Hydration so upgrading
// feels like this exact screen waking up):
//   1. Hero: today's ounces toward an editable local goal (default 64 oz)
//   2. Locked score teaser (dimmed arc + lock) -> opens the WTR+ paywall
//   3. Source cards: tap a size to log (same server call as MyIntake.jsx)
//   4. 7-day mini history (free endpoint returns logged-only bars)
//   5. Biometrics CTA -> paywall
//
// Hard rules on this screen (tier boundary, honesty canon):
//   - No hydration score, no "projected", no confidence pills.
//   - Absorption language appears ONLY inside the locked teaser copy, where
//     it does conversion work attached to the upgrade tap.
//   - The free goal is a client-side preference (localStorage), by design:
//     the adaptive server target is WTR+ ("Adaptive range, not a fixed
//     threshold" belongs to the paid tier).
//
// Signed-out state: fully browsable. Soft banner + any log tap routes to the
// magic-link sign-in (onSignIn), never an ambush modal.
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import { authHeaders, isSignedIn, sessionEmail } from "../lib/auth";
import { API_BASE } from "../lib/apiConfig";
import AppHeader from "./AppHeader";
import PaywallScreen from "./PaywallScreen";

const C = {
  white: "#FFFFFF", bg: "#F8FAFB", border: "#E8EEF2",
  blue: "#51B0E6", blueDark: "#2A8FCA", blueLight: "#EDF6FC",
  body: "#1A2535", muted: "#6B7A8D", cardGray: "#F0F1F3",
  green: "#1E8A4C", warning: "#F29423",
};

const OZ_PER_L = 33.814;
const TZ = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch (e) { return ""; } })();

const GOAL_KEY = "wtr_free_goal_oz";
const GOAL_DEFAULT = 64;
const GOAL_MIN = 32;
const GOAL_MAX = 200;
const GOAL_STEP = 8;

const SOURCES = [
  { key: "hub",      api: "WTR HUB",  label: "Generosity\u2122 Hub Water", short: "Hub water", hint: "Pure RO \u00B7 Alkaline", hero: true,
    sizes: [{ oz: 8, v: "Glass" }, { oz: 12, v: "Cup" }, { oz: 16, v: "Bottle" }, { oz: 24, v: "Large" }] },
  { key: "filtered", api: "Filtered", label: "Filtered Water", short: "Filtered", hint: "Standard filtration",
    sizes: [{ oz: 8, v: "Glass" }, { oz: 12, v: "Cup" }, { oz: 16, v: "Bottle" }] },
  { key: "tap",      api: "Tap",      label: "Tap Water", short: "Tap water", hint: "Unfiltered",
    sizes: [{ oz: 8, v: "Glass" }, { oz: 12, v: "Cup" }, { oz: 16, v: "Bottle" }] },
];

function readGoal() {
  try {
    const v = parseInt(localStorage.getItem(GOAL_KEY), 10);
    if (Number.isFinite(v) && v >= GOAL_MIN && v <= GOAL_MAX) return v;
  } catch (e) {}
  return GOAL_DEFAULT;
}

function identityEmail() {
  const s = sessionEmail && sessionEmail();
  if (s) return String(s).trim().toLowerCase();
  try {
    const p = JSON.parse(localStorage.getItem("wtr_profile") || "{}");
    return (p.email || "").trim().toLowerCase();
  } catch (e) { return ""; }
}

export default function MyIntakeScreen({ onMenu, onSignIn, onOpenProfile, onUpgrade }) {
  const signedIn = isSignedIn();
  const email = identityEmail();
  const validEmail = /.+@.+\..+/.test(email);

  const [totalOz, setTotalOz] = useState(0);
  const [loading, setLoading] = useState(true);
  const [needsProfile, setNeedsProfile] = useState(false);
  const [goal, setGoal] = useState(readGoal());
  const [editGoal, setEditGoal] = useState(false);
  const [busy, setBusy] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [flash, setFlash] = useState(null);
  const [history, setHistory] = useState(null); // [{date, logged}]
  const [showPaywall, setShowPaywall] = useState(false);

  const showFlash = (text, tone = "good") => { setFlash({ text, tone }); setTimeout(() => setFlash(null), 1800); };

  const setGoalClamped = (v) => {
    const g = Math.min(GOAL_MAX, Math.max(GOAL_MIN, v));
    setGoal(g);
    try { localStorage.setItem(GOAL_KEY, String(g)); } catch (e) {}
  };

  // ── Today's total (free endpoint, no score, no target) ─────────────────────
  const refreshToday = useCallback(async () => {
    if (!signedIn || !validEmail) { setLoading(false); return; }
    try {
      const res = await fetch(API_BASE + "/api/wtr/hydration/intake/today?units=oz", {
        headers: authHeaders({ Accept: "application/json" }),
      });
      if (res.status === 404) { setNeedsProfile(true); setTotalOz(0); return; }
      const json = await res.json();
      if (res.ok && json.ok) { setTotalOz(Math.max(0, Math.round(json.total || 0))); setNeedsProfile(false); }
    } catch (e) { /* keep last known total; logging still works */ }
    finally { setLoading(false); }
  }, [signedIn, validEmail]);

  // ── 7-day mini history (server clamps free tier to 7, logged-only) ─────────
  const refreshHistory = useCallback(async () => {
    if (!signedIn || !validEmail) return;
    try {
      const res = await fetch(
        API_BASE + "/api/wtr/hydration/history?units=oz&days=7&email=" + encodeURIComponent(email),
        { headers: authHeaders({ Accept: "application/json" }) }
      );
      const json = await res.json();
      if (res.ok && json.ok && Array.isArray(json.snapshotByDay)) {
        setHistory(json.snapshotByDay.slice(-7));
      }
    } catch (e) { /* history is decorative; never block logging on it */ }
  }, [signedIn, validEmail, email]);

  useEffect(() => { refreshToday(); refreshHistory(); }, [refreshToday, refreshHistory]);

  // ── Logging (same server contract as MyIntake.jsx) ─────────────────────────
  const post = async (apiSource, amountOz) => {
    const res = await fetch(API_BASE + "/api/wtr/hydration/intake", {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ email, source: apiSource, amount: amountOz, unit: "oz", tz: TZ }),
    });
    return res.json().then((data) => ({ ok: res.ok, data }));
  };

  const logDrink = async (src, oz) => {
    if (!signedIn || !validEmail) { onSignIn && onSignIn(); return; }
    if (busy) return;
    setBusy(src.key + "-" + oz);
    try {
      const { ok, data } = await post(src.api, oz);
      if (ok && data.ok) {
        setTotalOz((t) => Math.max(0, t + oz));
        setUndoStack((s) => [...s, { key: src.key, oz }]);
        setNeedsProfile(false);
        showFlash("Added " + oz + " oz \u00B7 " + src.short, "good");
        refreshHistory();
      } else if (data.error === "profile not found") {
        setNeedsProfile(true);
        showFlash("Set up your Profile first", "warn");
      } else {
        showFlash("Could not log that, try again", "warn");
      }
    } catch (e) { showFlash("Network error, try again", "warn"); }
    setBusy(null);
  };

  const undoLast = async () => {
    if (!undoStack.length || busy) return;
    const last = undoStack[undoStack.length - 1];
    const src = SOURCES.find((s) => s.key === last.key);
    if (!src) return;
    setBusy("undo");
    try {
      const { ok, data } = await post(src.api, -last.oz);
      if (ok && data.ok) {
        setTotalOz((t) => Math.max(0, t - last.oz));
        setUndoStack((s) => s.slice(0, -1));
        showFlash("Removed " + last.oz + " oz \u00B7 " + src.short, "good");
        refreshHistory();
      } else { showFlash("Could not undo, try again", "warn"); }
    } catch (e) { showFlash("Network error, try again", "warn"); }
    setBusy(null);
  };

  const pct = Math.min(100, Math.round((totalOz / goal) * 100));
  const totalL = (totalOz / OZ_PER_L).toFixed(1);
  const goalL = (goal / OZ_PER_L).toFixed(1);
  const maxBar = history && history.length
    ? Math.max(goal, ...history.map((d) => Math.round(d.logged || 0)))
    : goal;

  return (
    <div data-testid="my-intake-tab" style={{ fontFamily: "Montserrat, -apple-system, sans-serif", background: C.white, minHeight: "100%", color: C.body, display: "flex", flexDirection: "column", maxWidth: 480, margin: "0 auto", WebkitFontSmoothing: "antialiased" }}>
      <AppHeader variant="primary" title="My Intake" onMenu={onMenu} />

      <div style={{ padding: "16px 16px 120px", display: "flex", flexDirection: "column", gap: 14 }}>

        {/* Signed-out: soft ask, screen stays browsable */}
        {!signedIn && (
          <div style={{ background: "#FFF8EE", border: "1px solid #F2D9AE", borderRadius: 14, padding: "14px 16px" }}>
            <div style={{ fontSize: 13.5, color: "#7A5A1E", fontWeight: 600, lineHeight: 1.5, marginBottom: 10 }}>
              Sign in to start tracking. Your water log saves to your account and syncs across devices.
            </div>
            <button onClick={() => onSignIn && onSignIn()} data-testid="intake-signin"
              style={{ background: C.blue, color: "#fff", border: "none", borderRadius: 999, padding: "9px 22px", fontWeight: 700, fontSize: 13.5, cursor: "pointer", fontFamily: "inherit" }}>
              Sign in
            </button>
          </div>
        )}

        {/* Signed in but no profile row yet */}
        {signedIn && needsProfile && (
          <div style={{ background: "#FFF8EE", border: "1px solid #F2D9AE", borderRadius: 14, padding: "12px 16px", fontSize: 13.5, color: "#7A5A1E", fontWeight: 600, lineHeight: 1.5 }}>
            Finish your Profile so your log has a home.{" "}
            <button onClick={() => onOpenProfile && onOpenProfile()} style={{ background: "none", border: "none", color: C.blueDark, fontWeight: 800, cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: 13.5 }}>
              Open Profile
            </button>
          </div>
        )}

        {/* 1. HERO — today's ounces, the free tier's big number */}
        <div style={{ background: `linear-gradient(160deg, ${C.blueLight} 0%, ${C.white} 100%)`, borderRadius: 24, padding: "24px 20px 20px", border: `1px solid ${C.border}`, textAlign: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2, color: C.muted, textTransform: "uppercase", marginBottom: 6 }}>Today</div>
          <div style={{ fontSize: 56, fontWeight: 800, lineHeight: 1, color: C.body }}>
            {loading ? "\u2013" : totalOz}<span style={{ fontSize: 20, fontWeight: 700, color: C.muted, marginLeft: 6 }}>oz</span>
          </div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 8 }}>
            of{" "}
            <button onClick={() => setEditGoal((v) => !v)} data-testid="intake-goal"
              style={{ background: "none", border: "none", padding: 0, color: C.blueDark, fontWeight: 800, fontSize: 13, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>
              {goal} oz goal
            </button>
            {" \u00B7 "}{totalL} L of {goalL} L
          </div>
          {editGoal && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 14, marginTop: 12 }}>
              <button onClick={() => setGoalClamped(goal - GOAL_STEP)} style={{ width: 36, height: 36, borderRadius: 999, border: `1px solid ${C.border}`, background: C.white, fontSize: 18, fontWeight: 800, color: C.body, cursor: "pointer" }}>{"\u2212"}</button>
              <div style={{ fontSize: 15, fontWeight: 800, minWidth: 64 }}>{goal} oz</div>
              <button onClick={() => setGoalClamped(goal + GOAL_STEP)} style={{ width: 36, height: 36, borderRadius: 999, border: `1px solid ${C.border}`, background: C.white, fontSize: 18, fontWeight: 800, color: C.body, cursor: "pointer" }}>+</button>
            </div>
          )}
          <div style={{ height: 8, background: C.cardGray, borderRadius: 999, marginTop: 14, overflow: "hidden" }}>
            <div style={{ width: pct + "%", height: "100%", background: C.blue, borderRadius: 999, transition: "width .4s ease" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
            <span style={{ fontSize: 11.5, color: C.muted, fontWeight: 700 }}>{pct}%</span>
            {undoStack.length > 0 && (
              <button onClick={undoLast} disabled={!!busy} data-testid="intake-undo"
                style={{ background: "none", border: "none", color: C.muted, fontSize: 11.5, fontWeight: 700, cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
                Undo last
              </button>
            )}
          </div>
        </div>

        {/* 2. LOCKED SCORE TEASER — the upgrade surface, above the fold */}
        <button onClick={() => (onUpgrade ? onUpgrade() : setShowPaywall(true))} data-testid="locked-score-teaser"
          style={{ display: "block", width: "100%", textAlign: "center", background: C.cardGray, border: `1px solid ${C.border}`, borderRadius: 20, padding: "18px 18px 20px", cursor: "pointer", fontFamily: "inherit" }}>
          <div style={{ position: "relative", width: 120, height: 70, margin: "0 auto 10px" }}>
            <svg viewBox="0 0 120 70" style={{ width: "100%", height: "100%" }}>
              <path d="M 16 62 A 44 44 0 1 1 104 62" fill="none" stroke="#DDE3E9" strokeWidth="9" strokeLinecap="round" />
              <path d="M 16 62 A 44 44 0 0 1 60 18" fill="none" stroke={C.blue} strokeWidth="9" strokeLinecap="round" opacity="0.55" />
              <g transform="translate(60,44)" fill="none" stroke="#A6A8AB" strokeWidth="3">
                <rect x="-9" y="-4" width="18" height="15" rx="3.5" fill="#A6A8AB" stroke="none" />
                <path d="M -5.5 -4 v-3.5 a 5.5 5.5 0 0 1 11 0 v3.5" />
              </g>
            </svg>
          </div>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.body, marginBottom: 4 }}>Your Hydration Score is ready.</div>
          <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55, maxWidth: 300, margin: "0 auto" }}>
            Intake is what you drank. Absorption is what your body kept. Upgrade to see it.
          </div>
          <div style={{ display: "inline-block", marginTop: 10, background: C.blue, color: "#fff", borderRadius: 999, padding: "7px 20px", fontSize: 12.5, fontWeight: 800 }}>
            Unlock with WTR+
          </div>
        </button>

        {/* 3. SOURCES — tap a size to log */}
        <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, textAlign: "center" }}>
          Tap a size each time you drink. Resets each morning in your time zone.
        </div>
        {SOURCES.map((src) => (
          <div key={src.key} data-testid={"intake-source-" + src.key}
            style={{ background: src.hero ? C.blueLight : C.white, border: `1px solid ${src.hero ? "#CBE7F8" : C.border}`, borderRadius: 18, padding: "14px 14px 12px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
              <div>
                <span style={{ fontSize: 14.5, fontWeight: 800 }}>{src.label}</span>
                <div style={{ fontSize: 11.5, color: C.muted, fontWeight: 600 }}>{src.hint}</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {src.sizes.map((sz) => (
                <button key={sz.oz} onClick={() => logDrink(src, sz.oz)} disabled={!!busy}
                  data-testid={"intake-add-" + src.key + "-" + sz.oz}
                  style={{ flex: 1, background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: "10px 4px", cursor: "pointer", fontFamily: "inherit", opacity: busy ? 0.6 : 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: C.blueDark }}>+{sz.oz}</div>
                  <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{sz.v}</div>
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* 4. 7-DAY MINI HISTORY — logged bars only, no targets, no scores */}
        {signedIn && history && history.length > 0 && (
          <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 18, padding: "14px 16px" }}>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1, color: C.muted, textTransform: "uppercase", marginBottom: 12 }}>Last 7 days</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 72 }}>
              {history.map((d) => {
                const oz = Math.max(0, Math.round(d.logged || 0));
                const h = Math.max(4, Math.round((oz / maxBar) * 64));
                const wk = (() => { try { return new Date(d.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "narrow" }); } catch (e) { return ""; } })();
                return (
                  <div key={d.date} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    <div style={{ width: "100%", height: h, background: oz > 0 ? C.blue : C.cardGray, borderRadius: 6, opacity: oz > 0 ? 0.85 : 1 }} />
                    <div style={{ fontSize: 10, color: C.muted, fontWeight: 700 }}>{wk}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 5. BIOMETRICS CTA — routes to the paywall */}
        <button onClick={() => (onUpgrade ? onUpgrade() : setShowPaywall(true))} data-testid="intake-biometrics-cta"
          style={{ background: C.white, border: `1px dashed ${C.border}`, borderRadius: 16, padding: "14px 16px", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: C.body, marginBottom: 3 }}>Running without biometrics</div>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>Connect a wearable to fold sleep and readiness into your hydration. Part of WTR+.</div>
        </button>
      </div>

      {/* Flash toast */}
      {flash && (
        <div style={{ position: "fixed", bottom: 96, left: "50%", transform: "translateX(-50%)", zIndex: 500, background: flash.tone === "warn" ? "#7A5A1E" : C.body, color: "#fff", padding: "9px 18px", borderRadius: 999, fontSize: 12.5, fontWeight: 700, boxShadow: "0 6px 20px rgba(0,0,0,0.2)", whiteSpace: "nowrap" }}>
          {flash.text}
        </div>
      )}

      {/* Local paywall fallback when the shell does not pass onUpgrade */}
      {showPaywall && <PaywallScreen onClose={() => setShowPaywall(false)} />}
    </div>
  );
}
